import { useCallback, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash';
import { useRouter } from 'next/router';

import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import { useSetShouldRefreshVehicleList } from 'Stores/ListStore/ListStore';
import {
	useClearUnshortlistCache,
	useIsWaitingOnUnshortlistResponse,
	useRemoveEnquiriesFromShortlist,
	useSetIsWaitOnUnshortlistResponse,
	useUnshortlistCache,
	useUnshortlistFromCache,
} from 'Stores/ShortlistStore/ShortlistStore';
import { LIST_TYPES } from 'Utilities/consts';
import ROUTES from 'Utilities/routes';

import { useUnshortlistVehicleMutation } from './useUnshortlistVehicleMutation';

export const ROUTER_EVENT_TO_UNSHORTLIST = 'routeChangeStart';

type RouteChangeStartRef = ((targetUrl: string) => void) | null;
type HandleUnshortlistForEvents = () => void;

export const useUnshortlistVehicles = (refreshVehicleList: () => void): void => {
	const router = useRouter();
	const unshortlistFromCache = useUnshortlistFromCache();
	const clearUnshortlistCache = useClearUnshortlistCache();
	const removeEnquiriesFromShortlist = useRemoveEnquiriesFromShortlist();
	const unshortlistCache = useUnshortlistCache();
	const routeChangeStartRef = useRef<RouteChangeStartRef>(null);
	const isWaitingOnUnshortlistResponse = useIsWaitingOnUnshortlistResponse();
	const setIsWaitingOnUnshortlistResponse = useSetIsWaitOnUnshortlistResponse();
	const setShouldRefreshVehicleList = useSetShouldRefreshVehicleList();
	const {
		minWidth: { breakpointContent: isDesktop },
	} = useBreakpoint();
	const { mutateAsync } = useUnshortlistVehicleMutation(refreshVehicleList);

	// Deferred the unshortlist request until the next tick
	// This is prevent the unshortlist request from being cancelled when the user navigates to another page
	// We can remove the debounce once the redirect bugs of Browse Vehicles button is fixed
	const debounceUnshortlistMutation = useMemo(() => debounce(mutateAsync, 0), [mutateAsync]);

	const unshortlistVehicles = useCallback(() => {
		const escapeUnshortlistVehicles = isWaitingOnUnshortlistResponse || unshortlistCache.length === 0;
		if (escapeUnshortlistVehicles) return;
		setIsWaitingOnUnshortlistResponse(true);
		void debounceUnshortlistMutation({});
	}, [
		isWaitingOnUnshortlistResponse,
		unshortlistCache.length,
		setIsWaitingOnUnshortlistResponse,
		debounceUnshortlistMutation,
	]);

	const handleRouteChangeStart = useCallback(
		(targetUrl: string) => {
			const targetUrlWithoutQueryString = targetUrl.split('?')[0];
			const isShortlistListType = targetUrl.includes(`listType=${LIST_TYPES.shortlist}`);
			const shouldRefreshVehicleList = targetUrlWithoutQueryString === ROUTES.VEHICLE_LIST.as() && isShortlistListType;
			setShouldRefreshVehicleList(shouldRefreshVehicleList);
			unshortlistVehicles();
		},
		[unshortlistVehicles, setShouldRefreshVehicleList],
	);

	const handleClientSideRouteChanges = useCallback(() => {
		if (!routeChangeStartRef.current) {
			router.events.on(ROUTER_EVENT_TO_UNSHORTLIST, handleRouteChangeStart);
			routeChangeStartRef.current = handleRouteChangeStart;
		}

		const handleRouteChangesCleanup = () => {
			if (routeChangeStartRef.current) {
				router.events.off(ROUTER_EVENT_TO_UNSHORTLIST, routeChangeStartRef.current);
				routeChangeStartRef.current = null;
			}
		};
		return handleRouteChangesCleanup;
	}, [handleRouteChangeStart, router.events]);

	useEffect(() => {
		const handleRouteChangesCleanup = handleClientSideRouteChanges();
		return () => {
			handleRouteChangesCleanup();
		};
	}, [handleRouteChangeStart, router.events, unshortlistCache, unshortlistVehicles, handleClientSideRouteChanges]);

	const handleUnshortlistForEvents: HandleUnshortlistForEvents = useCallback(() => {
		setShouldRefreshVehicleList(true);
		unshortlistVehicles();
	}, [setShouldRefreshVehicleList, unshortlistVehicles]);

	const eventListenersMapping = useMemo(
		() => ({
			beforeunload: handleUnshortlistForEvents,
			blur: handleUnshortlistForEvents,
			pageshow: !isDesktop ? handleUnshortlistForEvents : undefined,
		}),
		[handleUnshortlistForEvents, isDesktop],
	);

	const handleUnshortlistEvents = useCallback(() => {
		Object.entries(eventListenersMapping).forEach(([event, handler]) => {
			if (handler) {
				window.addEventListener(event, handler);
			}
		});

		const handleUnshortlistEventsCleanup = () => {
			Object.entries(eventListenersMapping).forEach(([event, handler]) => {
				if (handler) {
					window.removeEventListener(event, handler);
				}
			});
		};
		return handleUnshortlistEventsCleanup;
	}, [eventListenersMapping]);

	// useEffect for adding event listeners
	useEffect(() => {
		const handleUnshortlistEventsCleanup = handleUnshortlistEvents();
		return () => {
			handleUnshortlistEventsCleanup();
		};
	}, [
		clearUnshortlistCache,
		handleUnshortlistForEvents,
		eventListenersMapping,
		refreshVehicleList,
		removeEnquiriesFromShortlist,
		unshortlistFromCache,
		unshortlistCache,
		unshortlistVehicles,
		handleUnshortlistEvents,
	]);
};
